import {BaseCard} from "../../Components/Cards/BaseCard";
import {createColumnHelper} from "@tanstack/react-table";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {useEffect, useState} from "react";
import {useBoundStore} from "../../Store/store";
import {BlackButton} from "../../Components/Buttons/BlackButton";
import {DangerButton} from "../../Components/Buttons/DangerButton";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import { faEye, faTrash, faCheck} from "@fortawesome/free-solid-svg-icons";
import {AdminRegistrationDetailDialog} from "../../Components/Dialogs/Registrations/AdminRegistrationDetailDialog";
import {ConfirmActionDialog} from "../../Components/Dialogs/Confirmation/ConfirmActionDialog";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";

export const Registrations = () => {
    const {t} = useTranslation();
    const registrations = useBoundStore(state => state.registrations)
    const registrationsMetaData = useBoundStore(state => state.registrationsMetaData)
    const getAllRegistrations = useBoundStore(state => state.getAllRegistrations)

    const rejectedRegistrations = useBoundStore(state => state.rejectedRegistrations)
    const rejectedRegistrationsMetaData = useBoundStore(state => state.rejectedRegistrationsMetaData)
    const getRejectedRegistrations = useBoundStore(state => state.getRejectedRegistrations)
    const forceDeleteRejectedFreelancer = useBoundStore(state => state.forceDeleteRejectedFreelancer)
    const acceptRejectedRegistration = useBoundStore(state => state.acceptRejectedRegistration)

    const [activeTab, setActiveTab] = useState('pending');
    const [currentPage, setCurrentPage] = useState(1);
    const [isOpen, setIsOpen] = useState(false);
    const [updateTable, setUpdateTable] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToAccept, setUserToAccept] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (activeTab === 'pending') {
            getAllRegistrations(currentPage)
        } else {
            getRejectedRegistrations(currentPage)
        }
    }, [getAllRegistrations, getRejectedRegistrations, updateTable, currentPage, activeTab])

    useEffect(() => {
        if (selectedUser && isOpen && activeTab === 'rejected') {
            const updatedUser = rejectedRegistrations.find(user => user.id === selectedUser.id);
            if (updatedUser) {
                setSelectedUser(updatedUser);
            }
        }
    }, [rejectedRegistrations, selectedUser?.id, isOpen, activeTab]);

    const columnHelper = createColumnHelper()

    const pendingColumns = [
        columnHelper.accessor('full_name', {
            header: () => t('tables.name'),
            cell: props => `${props.row.original.firstname}` + ' ' + `${props.row.original.lastname}`
        }),
        columnHelper.accessor('created_at', {
            header: () => t('registrations.applied_on'),
            cell: props => `${props.row.original.created_at?.formatted}`
        }),
        columnHelper.accessor('function', {
            header: t('tables.function'),
            cell: props => {
                if(props.cell.getValue() === 'A1') return t('functions.A1');
                if(props.cell.getValue() === 'A2') return t('functions.A2');
                if(props.cell.getValue() === 'A0') return t('functions.A0');
                if(props.cell.getValue() === 'A3') return t('functions.A3');
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <BlackButton onClick={() => {
                            setSelectedUser(props.row.original);
                            setIsOpen(true);
                        }}>
                            <FontAwesomeIcon icon={faEye} />
                        </BlackButton>
                    </div>
                )
            },
        })
    ]

    const rejectedColumns = [
        columnHelper.accessor('full_name', {
            header: () => t('tables.name'),
            cell: props => `${props.row.original.firstname}` + ' ' + `${props.row.original.lastname}`
        }),
        columnHelper.accessor('created_at', {
            header: () => t('registrations.applied_on'),
            cell: props => `${props.row.original.created_at?.formatted}`
        }),
        columnHelper.accessor('function', {
            header: t('tables.function'),
            cell: props => {
                if(props.cell.getValue() === 'A1') return t('functions.A1');
                if(props.cell.getValue() === 'A2') return t('functions.A2');
                if(props.cell.getValue() === 'A0') return t('functions.A0');
                if(props.cell.getValue() === 'A3') return t('functions.A3');
            },
        }),
        columnHelper.accessor('rejection_remark', {
            header: t('general.fields.rejection_remark'),
            cell: props => {
                const remark = props.cell.getValue();
                if (!remark) return '-';
                return remark.length > 50 ? remark.substring(0, 50) + '...' : remark;
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-2'}>
                        <BlackButton onClick={() => {
                            setSelectedUser(props.row.original);
                            setIsOpen(true);
                        }}>
                            <FontAwesomeIcon icon={faEye} />
                        </BlackButton>
                        <PrimaryButton onClick={() => {
                            setUserToAccept(props.row.original);
                            setIsAcceptDialogOpen(true);
                        }}>
                            <FontAwesomeIcon icon={faCheck} />
                        </PrimaryButton>
                        <DangerButton onClick={() => {
                            setUserToDelete(props.row.original);
                            setIsDeleteDialogOpen(true);
                        }}>
                            <FontAwesomeIcon icon={faTrash} />
                        </DangerButton>
                    </div>
                )
            },
        })
    ]

    const handleForceDelete = async () => {
        if (!userToDelete) return;

        setIsLoading(true);
        try {
            const response = await forceDeleteRejectedFreelancer(userToDelete.id);
            if (response) {
                toast.success(t('registrations.toast_deleted'));
                setUpdateTable(!updateTable);
            } else {
                toast.error(t('registrations.toast_delete_error'));
            }
        } catch (error) {
            toast.error(t('general.errors.generic'));
        } finally {
            setIsLoading(false);
            setIsDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    };

    const handleAcceptRejected = async () => {
        if (!userToAccept) return;

        setIsLoading(true);
        try {
            const response = await acceptRejectedRegistration(userToAccept.id, {
                hourly_rate: userToAccept.hourly_rate
            });
            if (response) {
                toast.success(t('registrations.toast_accepted'));
                setUpdateTable(!updateTable);
            } else {
                toast.error(t('registrations.toast_accept_error'));
            }
        } catch (error) {
            toast.error(t('general.errors.generic'));
        } finally {
            setIsLoading(false);
            setIsAcceptDialogOpen(false);
            setUserToAccept(null);
        }
    };

    return (
        <div>
            <div className={'mb-4'}>
                <div className="flex space-x-2">
                    <button
                        onClick={() => {
                            setActiveTab('pending');
                            setCurrentPage(1);
                        }}
                        className={`px-6 py-2 rounded-full font-medium transition-colors ${
                            activeTab === 'pending'
                                ? 'bg-black text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        {t('general.status.pending')}
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('rejected');
                            setCurrentPage(1);
                        }}
                        className={`px-6 py-2 rounded-full font-medium transition-colors ${
                            activeTab === 'rejected'
                                ? 'bg-black text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        {t('general.status.rejected')}
                    </button>
                </div>
            </div>
            <BaseCard>
                <TanStackTable
                    columns={activeTab === 'pending' ? pendingColumns : rejectedColumns}
                    tableData={activeTab === 'pending' ? registrations : rejectedRegistrations}
                    forceNewData={updateTable}
                    laravelPaginationMetaData={activeTab === 'pending' ? registrationsMetaData : rejectedRegistrationsMetaData}
                    onChangePage={(page) => {
                        setCurrentPage(page + 1)
                    }}
                />
            </BaseCard>

            <AdminRegistrationDetailDialog
                open={isOpen}
                user={selectedUser ?? {}}
                onClose={() => {
                    setSelectedUser(null);
                    setIsOpen(false);
                }}
                onUpdate={() => setUpdateTable(!updateTable)}
                isRejected={activeTab === 'rejected'}
            />

            <ConfirmActionDialog
                open={isDeleteDialogOpen}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setUserToDelete(null);
                }}
                onConfirm={handleForceDelete}
                title={t('registrations.delete_title')}
                message={t('registrations.delete_message', {name: `${userToDelete?.firstname} ${userToDelete?.lastname}`})}
                subMessage={t('registrations.delete_sub_message')}
                loading={isLoading}
            />

            <ConfirmActionDialog
                open={isAcceptDialogOpen}
                onClose={() => {
                    setIsAcceptDialogOpen(false);
                    setUserToAccept(null);
                }}
                onConfirm={handleAcceptRejected}
                title={t('registrations.accept_title')}
                message={t('registrations.accept_message', {name: `${userToAccept?.firstname} ${userToAccept?.lastname}`})}
                subMessage={t('registrations.accept_sub_message')}
                loading={isLoading}
            />
        </div>
    )
}